import React, { useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useIntl } from "umi";
import classnames from "classnames";
import { BaseTable, features, useTablePipeline } from 'ali-react-table';
import { ConfigProvider, Pagination } from "tea-component";
import AliTableCell from "./components/AliTableCell";
import MultipleSortHeaderCell from "./components/MultipleSortHeaderCell";
import HeaderCellBubble from "./components/HeaderCellBubble";
import { PAGE_INDEX, PAGE_SIZE, PAGE_SIZE_OPTIONS, TABLE_DEFAULT_STYLE } from "./consts";
import { getPaginatedData } from "./utils";
import {
  applyMergeSameCellsToColumns,
  computeMergeRowSpans,
  shouldApplyMergeSameCells,
} from './utils/computeMergeRowSpans';
import { IAliTableAnalyticsProps, IAliTableAnalyticsRef, TSortItem } from "./types";
import './index.scss';

/**
 * 列顺序 feature
 * 根据 pipeline state 中的 columnOrder 重新排列列
 */
function columnOrder() {
  const stateKey = 'columnOrder';
  return function columnOrderFeature(pipeline: any) {
    // 从 pipeline state 读取列顺序
    const orderCodes = pipeline.getStateAtKey(stateKey) || [];
    
    if (orderCodes.length === 0) {
      return pipeline; // 没有列顺序，直接返回
    }
    
    // 获取当前列
    const currentColumns = pipeline.getColumns();
    if (!currentColumns || currentColumns.length === 0) {
      return pipeline;
    }

    // 分组表头场景下 columns 可能存在嵌套 children。
    // 需求要求 applyColumnOrder 在分组场景下失效，以避免破坏组头/叶子结构。
    const hasNestedColumns = currentColumns.some((col: any) => Array.isArray(col?.children) && col.children.length > 0);
    if (hasNestedColumns) {
      return pipeline;
    }
    
    // 根据 orderCodes 重新排列列
    const orderSet = new Set(orderCodes);
    const columnsMap = new Map(currentColumns.map((col: any) => [col?.code, col]));
    const orderedColumns = orderCodes
      .map((code: string) => columnsMap.get(code))
      .filter(Boolean);
    const restColumns = currentColumns.filter((col: any) => !orderSet.has(col?.code));
    
    // 应用新的列顺序
    pipeline.columns([...orderedColumns, ...restColumns]);
    
    return pipeline;
  };
}

/**
 * 表格功能
 *   * 排序(多列排序, 默认带有弹窗交互) (报表编辑不可以)
 *   * 前端分页
 *   * 分页默认每页50条, ...
 *   * 拖拽调整列宽 (可控的, 图表可以, 报表不可以)
 *   * 表头弹窗展示描述 (表头还有排序弹窗)
 *   * 底部数据增加统计值
 *   * 单元格展示url
 *
 *
 */

// sorts 外界控制排序 (但是又想表格内部排序 不会反复触发渲染, 但要传递给外界进行保存)(可以通过ref传递给外界改变排序的方法)
  
// pageConfig  分页配置
// defaultPageSize 默认每页条数, 默认是50
// statisticsData 底部统计值 (可以考虑外界传入pipeline的处理方法)
// config 表格展示配置 (图表配置)(可以考虑外界传入pipeline的处理方法)
//    config.
// 用户定义的默认  列宽
// 拖拽列宽功能(可以考虑外界传入pipeline的处理方法)

const AliTableAnalytics: React.ForwardRefRenderFunction<IAliTableAnalyticsRef, IAliTableAnalyticsProps> = (props, ref) => {
  const {dataSource, columns, primaryKey, className, headerCellBubbleMap, appendUnsortedData = [], sortConfig, pagination, columnResize, headerBubbleConfig, clientPagingDisabled, mergeSameCells, hasCrosstab = false, isEn = false, ...restProps} = props;
  const { locale, formatMessage } = useIntl();
  const teaLocale = locale === 'zh-CN' ? 'zh' : 'en';
  const divRef = useRef(null);
  /** 分页 start      */
  const [page, setPage] = useState<{size?: number; index?: number;}>({size: pagination?.defaultPageSize || PAGE_SIZE, index: PAGE_INDEX});
  const total = (dataSource?.length ?? 0) + (appendUnsortedData?.length ?? 0);
  /** 分页 end       */
  
  const safeDataSource = Array.isArray(dataSource) ? dataSource : [];
  const safeAppendUnsortedData = Array.isArray(appendUnsortedData) ? appendUnsortedData : [];
  // 为每行每个字段对象注入 originalIndex，供排序 tie-breaker 使用
  const dataSourceWithIndex = safeDataSource.map((row, rowIdx) => {
    const newRow: Record<string, any> = {};
    for (const key of Object.keys(row)) {
      const cell = row[key];
      if (cell && typeof cell === 'object') {
        newRow[key] = { ...cell, originalIndex: rowIdx };
      } else {
        newRow[key] = cell;
      }
    }
    return newRow;
  });
  const pipeline = useTablePipeline().input({ dataSource: dataSourceWithIndex, columns })
    .primaryKey(primaryKey); // TODO 测试后主要看一下primaryKey
  
  /** 列顺序 start (必须在排序和列宽之前执行) */
  pipeline.use(columnOrder());
  /** 列顺序 end */
  
  /** 排序 start (必须先排序后分页) */
  pipeline.use(features.sort({
    mode: 'multiple',
    keepDataSource: false,
    highlightColumnWhenActive: false,
    orders: ['desc', 'asc', 'none'],
    // defaultSorts: sortConfig?.defaultSorts || [],
    SortHeaderCell: MultipleSortHeaderCell,
    ...sortConfig
  }));
  /** 排序 end */
  
  /** 拖拽列宽 start */
  columnResize?.isUse && pipeline.use(features.columnResize({
    fallbackSize: 180,
    minSize: 60,
    maxSize: 10000,
    handleBackground: 'transparent',
    handleHoverBackground: '#6a96f5',
    handleActiveBackground: '#ebecf1',
    ...columnResize,
  }));
  /** 拖拽列宽 end */
  
  /** 前端分页 start (排序之后再分页) */
  const tableProps = pipeline.getProps();
  const pipelineDataSource = Array.isArray(tableProps?.dataSource) ? tableProps.dataSource : [];

  const dimensionColumnCodes = useMemo(
    () => columns.filter(column => column.type === 1).map(column => column.code),
    [columns],
  );

  const pageSize = page.size || PAGE_SIZE;
  const pageIndex = page.index || PAGE_INDEX;
  const pageStartIndex = (pageIndex - 1) * pageSize;

  // 仅对当前页数据分页；统计行仅在末页追加到底部
  const pageDataRows = clientPagingDisabled
    ? pipelineDataSource
    : getPaginatedData(pipelineDataSource, pageSize, pageIndex);

  const isLastPage =
    clientPagingDisabled
    || pageStartIndex + pageDataRows.length >= pipelineDataSource.length;

  const displayData = isLastPage
    ? [...pageDataRows, ...safeAppendUnsortedData]
    : pageDataRows;

  const mergeLocaleEn = isEn || !!columns[0]?.isEn;
  const mergeRowSpansMap = useMemo(() => {
    if (
      !shouldApplyMergeSameCells(
        mergeSameCells,
        hasCrosstab,
        dimensionColumnCodes,
      )
    ) {
      return undefined;
    }
    // 分页后再算 rowSpan：每页独立合并，不与上一页延续
    return computeMergeRowSpans(pageDataRows, dimensionColumnCodes, mergeLocaleEn);
  }, [
    mergeSameCells,
    hasCrosstab,
    dimensionColumnCodes,
    pageDataRows,
    mergeLocaleEn,
  ]);

  pipeline.dataSource(displayData);

  // 合并单元格：通过 getCellProps 注入 rowSpan，走 BaseTable 内置 SpanManager
  if (mergeRowSpansMap && mergeRowSpansMap.size > 0) {
    pipeline.mapColumns(cols =>
      applyMergeSameCellsToColumns(cols, mergeRowSpansMap),
    );
  }
  /** 前端分页 end */

  // rowSpan 与纵向虚拟滚动不兼容（≥100 行自动开启虚拟滚动会导致列错位）
  const isMergeActive = !!mergeRowSpansMap && mergeRowSpansMap.size > 0;
  const tableUseVirtual = isMergeActive
    ? { vertical: false as const, horizontal: 'auto' as const, header: false as const }
    : undefined;
  
  useImperativeHandle(ref, () => ({
    // 对外提供修改排序方法 (如果以后需要其他表格状态修改, 也可以通过这里对外提供)
    changeSorts: (newSorts: TSortItem[]) => {
      // 修改排序
      pipeline?.setStateAtKey('sort', newSorts);
    },
    applyColumnOrder: (orderCodes: string[]) => {
      if (!Array.isArray(orderCodes) || orderCodes.length === 0) {
        return;
      }
      // 使用 setStateAtKey 存储列顺序，会自动触发重新渲染和 feature 重新执行
      pipeline?.setStateAtKey('columnOrder', orderCodes);
    },
    changePageSize: (pageSize: number) => {
      setPage({size: pageSize, index: 1});
    },
    getPageSize: () => page.size,
    // getTableState: () => {
    //   const state = {
    //     sorts: pipeline?.getStateAtKey('sort'),
    //     columnWidths: pipeline?.getStateAtKey('columnResize'),
    //   };
    //   console.log("** 表格state", state);
    //   return state;
    // }
  }));
  
  return (<div
    ref={divRef}
    // className={classnames('db-alitable-container', className)}
    className={classnames('dbl-ali-analysis-table-wrapper', className)}
    {...restProps}
  >
    <BaseTable
      {...pipeline.getProps()}
      useVirtual={tableUseVirtual ?? 'auto'}
      className={'dbl-ali-analysis-table'}
      style={{
        maxHeight: '100%',
        width: '100%',
        overflow: 'auto',
        ...TABLE_DEFAULT_STYLE,
      }}
      components={{
        // @ts-ignore
        Cell: AliTableCell,
      }}
    />
    <ConfigProvider locale={teaLocale}>
      <Pagination
        // pageCountChangingResetType={'last'}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        pageSize={page.size || PAGE_SIZE}
        pageIndex={page.index || PAGE_INDEX}
        recordCount={total}
        stateTextVisible={true}
        stateText={formatMessage(
          { id: 'dbi_pagination_total' },
          { total },
        )}
        {...pagination}
        className={classnames('dbl-ali-analysis-table-pagination', pagination?.className)}
        onPagingChange={(query) => {
          setPage({size: query.pageSize, index: query.pageIndex});
          pagination?.onPagingChange?.(query);
        }}
      />
    </ConfigProvider>
    <HeaderCellBubble headerRef={divRef} headerCellBubbleMap={headerCellBubbleMap} {...headerBubbleConfig} />
  </div>);
};

export default React.forwardRef(AliTableAnalytics);

